import { Business, KeyboardArrowDown } from "@mui/icons-material";
import { Button, ListItemText, Menu, MenuItem } from "@mui/material";
import React, { FunctionComponent, useEffect, useState } from "react";
import { AppStore } from "../../AppStore";
import { CompanyService } from "../../services/company/CompanyService";
import { Company } from "../../services/company/models/Company";

/**
 * Lets a user who belongs to more than one company choose which one the
 * company and system areas are scoped to. Renders nothing for users with a
 * single company (or none), so the bar is unchanged for them.
 */
const CompanySwitcher: FunctionComponent = () => {
  const [account, accountActions] = AppStore.account.use();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const companyIds = account.user?.getCompanyIds() ?? [];

  useEffect(() => {
    if (companyIds.length < 2) return;

    Promise.all(companyIds.map((id) => CompanyService.getOne(id)))
      .then((results) => setCompanies(results.filter((c) => c) as Company[]))
      .catch(() => setCompanies([]));
  }, [companyIds.join(",")]);

  if (companyIds.length < 2) return null;

  const selected =
    companies.find((c) => c.id === account.selectedCompanyId) ?? companies[0];

  const handleSelect = (company: Company) => {
    accountActions.setSelectedCompany(company.id);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        color="inherit"
        startIcon={<Business />}
        endIcon={<KeyboardArrowDown />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ ml: 1 }}
      >
        {selected?.name ?? "Select company"}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {companies.map((company) => (
          <MenuItem
            key={company.id}
            selected={company.id === account.selectedCompanyId}
            onClick={() => handleSelect(company)}
          >
            <ListItemText primary={company.name} secondary={company.location} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CompanySwitcher;
